import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, Layers, Plus, Printer } from 'lucide-react'

import type { ListLotsParams } from '@/lib/api/packaging-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    LOT_CARD_CODE_CLASS,
    LotListCard,
} from '@/components/common/LotListCard'
import { RefreshButton } from '@/components/common/refresh-button'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { PackagingLotSearchInput } from '@/components/packaging/packaging-lot-search-input'
import { PositionPicker } from '@/components/warehouse-positions/position-picker'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { usePrintPackagingLotLabel } from '@/hooks/use-lot-label-print'
import {
    usePackagingLots,
    usePackagingLotsInfinite,
} from '@/hooks/use-packaging-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface PackagingLotsSearch {
    search?: string
    position_id?: string
    page?: number
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/lots/',
)({
    component: Page,
    validateSearch: (search: Record<string, unknown>): PackagingLotsSearch => {
        const out: PackagingLotsSearch = {}
        if (typeof search.search === 'string' && search.search) {
            out.search = search.search
        }
        if (typeof search.position_id === 'string' && search.position_id) {
            out.position_id = search.position_id
        }
        const p =
            typeof search.page === 'number' ? search.page : Number(search.page)
        if (Number.isFinite(p) && p > 1) out.page = p
        return out
    },
})

function Page() {
    const search = Route.useSearch()
    const navigate = useNavigate({ from: '/packaging-warehouse/lots/' })
    const setSearch = (next: PackagingLotsSearch) => {
        void navigate({ search: () => next, replace: false })
    }
    const setPage = (p: number) =>
        setSearch({ ...search, page: p > 1 ? p : undefined })

    const params = useMemo<ListLotsParams>(
        () => ({
            page: search.page ?? 1,
            per_page: 50,
            ...(search.search ? { search: search.search } : {}),
            ...(search.position_id ? { position_id: search.position_id } : {}),
        }),
        [search],
    )
    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = usePackagingLots(params, {
        enabled: !isMobile,
    })

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({
            per_page: 50,
            ...(search.search ? { search: search.search } : {}),
            ...(search.position_id ? { position_id: search.position_id } : {}),
        }),
        [search.search, search.position_id],
    )
    const infinite = usePackagingLotsInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileLots = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const lots = data?.data ?? []
    const total = data?.meta?.total ?? 0
    const page = search.page ?? 1
    const filterActive = !!search.search || !!search.position_id || page > 1
    const clearFilter = () => setSearch({})
    const printMut = usePrintPackagingLotLabel()

    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    // debounce=true cho desktop (gõ tới đâu tìm tới đó), false cho mobile —
    // giữ nguyên hành vi cũ là chỉ tìm khi nhấn Enter.
    const renderFilters = (debounce: boolean) => (
        <>
            <div>
                <Label className="mb-1 block">
                    {m.pkl_lots_search_label()}
                </Label>
                <PackagingLotSearchInput
                    value={search.search ?? ''}
                    debounce={debounce}
                    placeholder={m.pkl_lots_search_placeholder()}
                    onCommit={(val) =>
                        setSearch({
                            ...search,
                            search: val || undefined,
                            page: undefined,
                        })
                    }
                />
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.lot_list_filter_position()}
                </Label>
                <PositionPicker
                    lotType="packaging"
                    value={search.position_id}
                    onChange={(positionId) =>
                        setSearch({
                            ...search,
                            position_id: positionId || undefined,
                            page: undefined,
                        })
                    }
                    placeholder={m.lot_list_filter_all_positions()}
                    allowClear
                />
            </div>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<Layers className="size-5 shrink-0" />}
                title={m.pkl_lots_title()}
                utilityActions={
                    <>
                        <RefreshButton
                            onClick={() => void refetch()}
                            loading={isFetching}
                        />
                        <ClearFilterButton
                            active={filterActive}
                            onClear={clearFilter}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setFilterDialogOpen(true)}
                        >
                            <Filter className="size-4" />
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/packaging-warehouse/stock-in-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pkl_lots_create_pnk()}
                        </Link>
                    </Button>
                }
            />

            <div className="hidden md:grid gap-3 md:grid-cols-2">
                {renderFilters(true)}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.pkl_lots_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters(false)}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1"
                            onClick={() => {
                                clearFilter()
                                setFilterDialogOpen(false)
                            }}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileLots.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.pkl_lots_empty()}
                    </div>
                ) : (
                    <>
                        {mobileLots.map((lot) => {
                            const attributeSub = [
                                lot.warehouse_name,
                                lot.position_code,
                            ]
                                .filter(Boolean)
                                .join(' · ')
                            return (
                                <LotListCard
                                    key={lot.id}
                                    onClick={() =>
                                        void navigate({
                                            to: '/packaging-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                    code={
                                        <Link
                                            to="/packaging-warehouse/lots/$id"
                                            params={{ id: lot.id }}
                                            className={LOT_CARD_CODE_CLASS}
                                        >
                                            {lot.code}
                                        </Link>
                                    }
                                    attribute={lot.material_name ?? '—'}
                                    attributeSub={
                                        attributeSub ? attributeSub : undefined
                                    }
                                    quantityAvailable={lot.quantity_available}
                                    quantityUnit={lot.material_unit}
                                    createdAt={lot.created_at}
                                    actions={
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            aria-label={m.lot_label_print_btn()}
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    }
                                />
                            )
                        })}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>{m.pkl_lots_col_material()}</TableHead>
                            <TableHead>{m.pkl_lots_col_warehouse()}</TableHead>
                            <TableHead>{m.lot_list_col_position()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_lots_col_available()}
                            </TableHead>
                            <TableHead>{m.sl_lots_col_created_at()}</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : lots.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.pkl_lots_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            lots.map((lot) => (
                                <TableRow
                                    key={lot.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/packaging-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/packaging-warehouse/lots/$id"
                                            params={{ id: lot.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {lot.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{lot.material_name}</TableCell>
                                    <TableCell>{lot.warehouse_name}</TableCell>
                                    <TableCell className="text-sm">
                                        {lot.position_code ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(lot.quantity_available)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(lot.created_at)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
